import React from 'react';
import { Box, Typography, Container, Grid, Card, CardContent } from '@mui/material';
import { motion } from 'framer-motion';
import { 
  FaCode, 
  FaDatabase, 
  FaArrowTrendUp, 
  FaShieldHalved, 
  FaHeartPulse, 
  FaLightbulb, 
  FaBrain
} from 'react-icons/fa6';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface ServiciosGridProps {
  useHashLinks?: boolean;
}

export default function ServiciosGrid({ useHashLinks = false }: ServiciosGridProps) {
  const { t } = useTranslation();

  const services = [
    {
      title: t('nav.servicios_list.desarrollo'),
      icon: <FaCode size={32} color="#0050d1" />,
      href: '/servicios/desarrollo',
      hashId: 'desarrollo'
    },
    {
      title: t('nav.servicios_list.migracion'),
      icon: <FaDatabase size={32} color="#06b6d4" />,
      href: '/servicios/migraciones',
      hashId: 'migracion'
    },
    {
      title: t('nav.servicios_list.digitalizacion'),
      icon: <FaArrowTrendUp size={32} color="#0050d1" />,
      href: '/servicios/transformacion',
      hashId: 'digitalizacion'
    },
    {
      title: t('nav.servicios_list.data'),
      icon: <FaShieldHalved size={32} color="#06b6d4" />,
      href: '/servicios/data-governance',
      hashId: 'data'
    },
    {
      title: t('nav.servicios_list.incident'),
      icon: <FaHeartPulse size={32} color="#0050d1" />,
      href: '/servicios/incident-manager',
      hashId: 'incident'
    },
    {
      title: t('nav.servicios_list.consultoria'),
      icon: <FaLightbulb size={32} color="#06b6d4" />,
      href: '/servicios/consultoria',
      hashId: 'consultoria'
    },
    {
      title: t('nav.servicios_list.ia'),
      icon: <FaBrain size={32} color="#0050d1" />,
      href: '/servicios/ia',
      hashId: 'ia'
    }
  ];

  return (
    <Box component="section" sx={{ py: 12, position: 'relative', bgcolor: '#F8F9FA' }}>
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 10 }}>
        <Box sx={{ mb: 8, maxWidth: '900px', mx: 'auto', textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <Typography 
              variant="h3" 
              component="h2" 
              sx={{ 
                fontWeight: 700, 
                color: '#0056b3',
                lineHeight: 1.3,
                letterSpacing: '-0.02em',
                fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.25rem' }
              }}
            >
          {t("pages.servicios.hero.subtitle")}
            </Typography>
          </motion.div>
        </Box>

        <Grid container spacing={{ xs: 4, md: 5 }}>
          {services.map((service, index) => {
            const targetHref = useHashLinks ? `#${service.hashId}` : service.href;
            
            return (
              <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  whileHover={{ y: -10, transition: { duration: 0.25, ease: "easeOut" } }}
                  style={{ height: '100%' }}
                >
                  <Card
                    component={Link}
                    to={targetHref}
                    elevation={0}
                    sx={{
                      textDecoration: 'none',
                      height: '100%',
                      minHeight: 220,
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: '1.75rem',
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: '0 4px 30px rgba(0,0,0,0.02)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 25px 60px rgba(10,17,40,0.08)',
                        '& .service-icon': {
                          transform: 'scale(1.1)',
                        },
                        '& .service-title': {
                          color: '#0050d1',
                        },
                        '& .explore-text': {
                          color: 'primary.main',
                        },
                        '& .explore-arrow': {
                          transform: 'translateX(0)',
                          opacity: 1,
                        }
                      }
                    }}
                  >
                    <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                      <Box
                        className="service-icon"
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: '1rem',
                          bgcolor: 'rgba(6, 182, 212, 0.08)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 3,
                          transition: 'transform 0.3s ease',
                        }}
                      >
                        {service.icon}
                      </Box>
                      
                      <Typography
                        className="service-title"
                        variant="h6"
                        component="h3"
                        sx={{
                          fontWeight: 700,
                          color: '#002766',
                          lineHeight: 1.3,
                          mb: 2,
                          transition: 'color 0.3s ease',
                          fontSize: { xs: '1rem', sm: '1.05rem', md: '1.125rem' }
                        }}
                      >
                        {service.title}
                      </Typography>

                      <Box
                        sx={{
                          mt: 'auto',
                          pt: 2,
                          borderTop: '1px solid',
                          borderColor: 'divider',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography
                          className="explore-text"
                          variant="overline"
                          sx={{
                            fontWeight: 700,
                            letterSpacing: '0.1em',
                            color: 'text.disabled',
                            transition: 'color 0.3s ease',
                          }}
                        >
                          {t("pages.servicios.hero.explore")}
                        </Typography>
                        <Box
                          className="explore-arrow"
                          sx={{
                            transform: 'translateX(-12px)',
                            opacity: 0,
                            transition: 'all 0.3s ease',
                            color: 'primary.main',
                            fontWeight: 'bold',
                          }}
                        >
                          →
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
}
